/**
 * All reads for the campaign-detail surface — hits the role-agnostic
 * `/api/v1/campaigns/{id}/*` BE surface introduced by the 2026-05-13
 * roster-tab unification. The BE applies per-role projection on the
 * `creator` block (admin sees it, brand doesn't) based on the JWT, so
 * the FE factory has no role parameter — same call shape for both roles.
 *
 * Grown incrementally across Phases A → D:
 *   - Phase A: listCreators + getCreatorsKPI       (this commit)
 *   - Phase B: listFinalized + getFinalizedKPI
 *   - Phase C: listScript / listContent / listLiveContent
 *   - Phase D: listRejected + campaign-level reads
 *
 * Once every tab consumer is on this factory, the legacy
 * createBrandCampaignDetailApi + createAdminCampaignDetailApi factories
 * retire (their /admin/* and /brand/* URLs go away on the BE side).
 */
import { apiClient } from '@discovr/common';
import { queryOptions } from '@tanstack/react-query';
import type {
  Campaign,
  CampaignEditRequest,
  CampaignListRow,
  CampaignPipeline,
  ContentDetail,
  PostNoteRequest,
  CreatorsTabKPI,
  DataResponse,
  FinalizedTabKPI,
  MessageAnchor,
  MessageView,
  ScriptVersionDetail,
  ScriptVersionListing,
  PaginatedResponse,
  RosterCounts,
  RosterEntryContent,
  RosterEntryCreators,
  RosterEntryFinalized,
  RosterEntryLive,
  RosterEntryRejected,
  RosterEntryScript,
  ScriptDetail,
  SentimentRosterDailyResponse,
  ThreadMessagesPage,
} from '@discovr/types';

import type { ThreadType } from './shared';

import { type PagedResult, dateRangeQs, getData, getOrNullOn404, parsePaged } from './shared';
import { campaignKeys, CONTENT_THREAD_TYPE, type SortParams } from '../queryKeys';

/**
 * The content thread's REST prefix. Endpoints hang off it (notes, uploads,
 * messages, files — see api/threadFiles.ts) so the segment is built in one
 * place instead of each caller rebuilding it by hand. Exported for
 * threadFiles.ts, the only other module allowed to depend on it.
 */
export const contentThreadPath = (entryId: string): string =>
  `/roster/${encodeURIComponent(entryId)}/threads/${CONTENT_THREAD_TYPE}`;


/**
 * GET /api/v1/campaigns
 *
 * Paginated campaign list with role-aware filtering done server-side
 * (admin sees all; brand_owner sees only their brand; creator sees
 * rostered). Same shape for both admin and brand callers (slim
 * CampaignListItem rows) — pages that previously hit /admin/campaigns
 * or /brand/campaigns share this endpoint now.
 */
export interface ListCampaignsParams {
  page?: number;
  limit?: number;
}

const listCampaigns = async (
  params: ListCampaignsParams = {},
): Promise<{ rows: CampaignListRow[]; total: number }> => {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  const res = await apiClient.get<PaginatedResponse<CampaignListRow>>(
    `/campaigns${query ? `?${query}` : ''}`,
  );
  return {
    rows: res.data.data,
    total: res.data.pagination?.total ?? res.data.data.length,
  };
};

/**
 * Type-safe `queryOptions` for the campaign list. Used by the brand
 * dashboard prefetch + the BrandCampaignsTab page; the admin app's
 * CampaignsPage / PaymentsPage / BrandDetailPage call `listCampaigns`
 * directly through their existing useQuery flows.
 */
export const campaignListOptions = (page: number, limit = 12) =>
  queryOptions({
    queryKey: campaignKeys.campaignList(page, limit),
    queryFn: () => listCampaigns({ page, limit }),
    staleTime: 30_000,
  });

/**
 * Fetch the ENTIRE campaign list (all pages) for client-side search /
 * pagination. Requests one large page; if the BE caps it below `total`, pages
 * through the remainder and concatenates. No `page` in the key — it's the whole
 * set, cached once under the ['campaigns-list','all'] prefix.
 */
const FETCH_ALL_PAGE_SIZE = 200;

const listAllCampaigns = async (): Promise<{ rows: CampaignListRow[]; total: number }> => {
  const first = await listCampaigns({ page: 1, limit: FETCH_ALL_PAGE_SIZE });
  let rows = first.rows;
  const total = first.total;
  let page = 2;
  // Page through any remainder. Break on an empty page so a stale `total` can't
  // spin the loop forever.
  while (rows.length < total) {
    const next = await listCampaigns({ page, limit: FETCH_ALL_PAGE_SIZE });
    if (next.rows.length === 0) break;
    rows = [...rows, ...next.rows];
    page += 1;
  }
  return { rows, total };
};

export const campaignListAllOptions = () =>
  queryOptions({
    queryKey: campaignKeys.campaignListFull(),
    queryFn: listAllCampaigns,
    staleTime: 30_000,
  });

/**
 * GET /api/v1/campaigns/{id}
 *
 * Single campaign detail — returns the Campaign exactly as the BE sends it.
 * The Campaign type matches the BE's CampaignDetails DTO directly:
 * brief + creator_preference + targeting are nested objects, not flat.
 */
const getCampaign = async (campaignId: string): Promise<Campaign> => {
  const res = await apiClient.get<DataResponse<Campaign>>(
    `/campaigns/${encodeURIComponent(campaignId)}`,
  );
  return res.data.data;
};

/**
 * PUT /api/v1/campaigns/{id}/status
 *
 * Stop or restart a campaign — admin/super-admin only (BE:
 * authz.ScopeCampaignStatusWrite). `body.confirm` carries the typed-
 * confirmation value (ActionConfirmDialog's `confirmText="CONFIRM"`
 * fixed-word gate) for the BE's confirm.Matches check — same requirement
 * in either direction (stop → restart).
 */
const updateCampaignStatus = async (
  campaignId: string,
  body: { status: 'stopped' | 'running'; confirm?: string },
): Promise<Campaign> => {
  const res = await apiClient.put<DataResponse<Campaign>>(
    `/campaigns/${encodeURIComponent(campaignId)}/status`,
    body,
  );
  return res.data.data;
};

/**
 * PUT /api/v1/campaigns/{id}/fetch-status
 *
 * Stop or resume background analytics fetching for a campaign — independent
 * of `status` — admin/super-admin only (BE: authz.ScopeCampaignStatusWrite).
 * `body.confirm` carries the typed-confirmation value (ActionConfirmDialog's
 * `confirmText="CONFIRM"` fixed-word gate) for the BE's confirm.Matches
 * check — same requirement in either direction (stop → resume).
 */
const updateCampaignFetchStatus = async (
  campaignId: string,
  body: { fetch_status: 'stopped' | 'active'; confirm?: string },
): Promise<Campaign> => {
  const res = await apiClient.put<DataResponse<Campaign>>(
    `/campaigns/${encodeURIComponent(campaignId)}/fetch-status`,
    body,
  );
  return res.data.data;
};

/**
 * PUT /api/v1/campaigns/{id}/pipeline
 *
 * Turn the script / content / brand safety stages on or off — admin only (BE:
 * authz.ScopeCampaignStatusWrite). 409 when a stage still has creators in it,
 * or another admin changed the pipeline first.
 */
const updateCampaignPipeline = async (
  campaignId: string,
  body: CampaignPipeline,
): Promise<CampaignPipeline> => {
  const res = await apiClient.put<DataResponse<CampaignPipeline>>(
    `/campaigns/${encodeURIComponent(campaignId)}/pipeline`,
    body,
  );
  return res.data.data;
};

/**
 * GET /api/v1/campaigns/{id}/edit-requests/current
 *
 * The campaign's open (not yet fully resolved) edit request, or null if none
 * exists. Powers both the brand's pending-review banner and the admin's
 * Changes-to-review panel — every FieldChange.comments entry (including a
 * rejection's reason) is visible to both sides, no gating.
 */
const getCurrentEditRequest = async (campaignId: string): Promise<CampaignEditRequest | null> => {
  const res = await apiClient.get<DataResponse<CampaignEditRequest | null>>(
    `/campaigns/${encodeURIComponent(campaignId)}/edit-requests/current`,
  );
  return res.data.data;
};

/**
 * GET /api/v1/campaigns/{id}/edit-requests/history
 *
 * Every edit request for the campaign (open + resolved), newest first.
 * Powers the brand's "Changes history" view.
 */
const listEditRequestHistory = async (campaignId: string): Promise<CampaignEditRequest[]> => {
  const res = await apiClient.get<DataResponse<CampaignEditRequest[]>>(
    `/campaigns/${encodeURIComponent(campaignId)}/edit-requests/history`,
  );
  return res.data.data;
};

/**
 * POST /api/v1/campaigns/{id}/edit-requests/{req_id}/fields/{field_path}/comments
 *
 * Appends one entry to a field's conversation thread. Either the submitter
 * or a reviewer can call this at any FieldChange status (pending, approved,
 * rejected, even after applied) — shared by both brand and admin capability
 * wiring, not role-gated client-side (the BE gates on campaign:read, which
 * both roles already hold to see the thread in the first place).
 */
const addFieldComment = async (
  campaignId: string,
  requestId: string,
  fieldPath: string,
  text: string,
): Promise<CampaignEditRequest> => {
  const res = await apiClient.post<DataResponse<CampaignEditRequest>>(
    `/campaigns/${encodeURIComponent(campaignId)}/edit-requests/${encodeURIComponent(requestId)}/fields/${encodeURIComponent(fieldPath)}/comments`,
    { text },
  );
  return res.data.data;
};

/** Pagination / status-filter params accepted by every roster list endpoint.
 *  Optional — the BE applies defaults (page=1, limit=20, no status filter). */
export interface UnifiedListParams extends SortParams {
  status?: string;
  page?: number;
  limit?: number;
  /** Finalized sub-tab filter — sent to /roster/finalized so the phase is
   *  filtered + paginated server-side (total/total_pages reflect the phase, not
   *  the whole finalized set). Ignored by every other roster list endpoint. */
  phase?: 'script' | 'content' | 'live' | 'hold';
  /** Case-insensitive substring on the creator handle / display name, applied
   *  server-side (regex over the denormalized fields). Supported on the Creators
   *  tab and the Finalized "All" sub-tab. */
  search?: string;
}

function unifiedRosterQs({ status, page = 1, limit = 20, sort_by, sort_dir, phase, search }: UnifiedListParams = {}): string {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status) qs.set('status', status);
  if (sort_by) qs.set('sort_by', sort_by);
  if (sort_dir) qs.set('sort_dir', sort_dir);
  if (phase) qs.set('phase', phase);
  if (search && search.trim()) qs.set('search', search.trim());
  return qs.toString();
}

/**
 * GET /api/v1/campaigns/{id}/roster/creators
 *
 * Returns the Creators-tab roster page. Each row is a RosterEntryCreators
 * — admin callers get the `creator` block populated; brand callers get
 * it absent (BE omits via omitempty).
 */
const listCreators = (
  campaignId: string,
  params: UnifiedListParams = {},
): Promise<PagedResult<RosterEntryCreators>> =>
  apiClient
    .get<PaginatedResponse<RosterEntryCreators>>(
      `/campaigns/${encodeURIComponent(campaignId)}/roster/creators?${unifiedRosterQs(params)}`,
    )
    .then((r) => parsePaged<RosterEntryCreators>(r.data));

/**
 * GET /api/v1/campaigns/{id}/roster-kpis/creators
 *
 * Returns the Creators-tab forecast (estimated_reach + estimated_budget +
 * creator_count). Tab-aggregated server-side; pagination-correct (covers
 * the full tab, not just one page). Scope is fixed — no ?status= filter.
 */
const getCreatorsKPI = async (campaignId: string): Promise<CreatorsTabKPI> => {
  const res = await apiClient.get<DataResponse<CreatorsTabKPI>>(
    `/campaigns/${encodeURIComponent(campaignId)}/roster-kpis/creators`,
  );
  return res.data.data;
};

/**
 * GET /api/v1/campaigns/{id}/roster/finalized
 *
 * Returns the Finalized-tab roster page (accepted_by_admin → live).
 * Deliverables are filtered server-side to the locked is_selected set
 * (one per row by V2 invariant). live_video is populated only on
 * status==live rows.
 */
const listFinalized = (
  campaignId: string,
  params: UnifiedListParams = {},
): Promise<PagedResult<RosterEntryFinalized>> =>
  apiClient
    .get<PaginatedResponse<RosterEntryFinalized>>(
      `/campaigns/${encodeURIComponent(campaignId)}/roster/finalized?${unifiedRosterQs(params)}`,
    )
    .then((r) => parsePaged<RosterEntryFinalized>(r.data));

/**
 * GET /api/v1/campaigns/{id}/roster-kpis/finalized
 *
 * Returns the Finalized-tab actuals — actual_views (sum of live_video
 * view counts, status==live only), locked_budget (sum across all
 * finalized rows), plus creator_count + live_creator_count. Different
 * shape from getCreatorsKPI because the two tabs answer different
 * questions (forecast vs realized).
 */
const getFinalizedKPI = async (campaignId: string): Promise<FinalizedTabKPI> => {
  const res = await apiClient.get<DataResponse<FinalizedTabKPI>>(
    `/campaigns/${encodeURIComponent(campaignId)}/roster-kpis/finalized`,
  );
  return res.data.data;
};

/**
 * GET /api/v1/campaigns/{id}/roster/counts
 *
 * Consolidated per-status + per-phase roster counts in ONE request. Replaces
 * the Creators tab's four limit:1 count queries and supplies the Finalized
 * sub-tab (All/Script/Content/Live) badges. Server-computed across the whole
 * tab, so counts are correct regardless of the current page.
 */
const getRosterCounts = async (campaignId: string): Promise<RosterCounts> => {
  const res = await apiClient.get<DataResponse<RosterCounts>>(
    `/campaigns/${encodeURIComponent(campaignId)}/roster/counts`,
  );
  return res.data.data;
};

/**
 * GET /api/v1/campaigns/{id}/roster/script
 *
 * Returns the Scripts-tab roster page (status ∈ {script_pending,
 * script_review}). Activity-sorted server-side. Each row carries
 * last_message + unread_count + the two thread-locked flags.
 * Script body + AI envelope come from `getScriptDetail` when the
 * user opens the review panel.
 */
const listScript = (
  campaignId: string,
  params: UnifiedListParams = {},
): Promise<PagedResult<RosterEntryScript>> =>
  apiClient
    .get<PaginatedResponse<RosterEntryScript>>(
      `/campaigns/${encodeURIComponent(campaignId)}/roster/script?${unifiedRosterQs(params)}`,
    )
    .then((r) => parsePaged<RosterEntryScript>(r.data));

/**
 * GET /api/v1/campaigns/{id}/roster/content
 *
 * Returns the Content-tab roster page (status ∈ {content_pending,
 * content_review}). Mirrors listScript plus `live_url` (the row's
 * content-version URL). Content body + AI envelope come from
 * `getContentDetail` when the dialog opens.
 */
const listContent = (
  campaignId: string,
  params: UnifiedListParams = {},
): Promise<PagedResult<RosterEntryContent>> =>
  apiClient
    .get<PaginatedResponse<RosterEntryContent>>(
      `/campaigns/${encodeURIComponent(campaignId)}/roster/content?${unifiedRosterQs(params)}`,
    )
    .then((r) => parsePaged<RosterEntryContent>(r.data));

// ─── Per-entry detail + thread endpoints (unified) ──────────────────────────
//
// All hit /api/v1/roster/{entry_id}/* — no /admin or /brand prefix.
// Throw on failure (no silent catch); React Query / callers handle
// the error so BE failures don't disappear into empty UI.

/**
 * GET /api/v1/roster/{entry_id}/script
 *
 * Full Script detail — markdown text + (when uploaded) AI analysis
 * envelope. Used by the Scripts tab + Finalized tab read-only panel
 * when the user opens the review dialog.
 */
const getScriptDetail = async (entryId: string): Promise<ScriptDetail> => {
  const res = await apiClient.get<DataResponse<ScriptDetail>>(
    `/roster/${encodeURIComponent(entryId)}/script`,
  );
  return res.data.data;
};

/**
 * GET /api/v1/roster/{entry_id}/content
 *
 * Full Content detail — video URL + (when uploaded) AI analysis
 * envelope. Used by the Content tab + Finalized tab read-only panel.
 */
const getContentDetail = async (entryId: string): Promise<ContentDetail> => {
  const res = await apiClient.get<DataResponse<ContentDetail>>(
    `/roster/${encodeURIComponent(entryId)}/content`,
  );
  return res.data.data;
};

const getContentVideoUrl = async (entryId: string): Promise<{ url: string }> => {
  const res = await apiClient.get<DataResponse<{ url: string }>>(
    `/roster/${encodeURIComponent(entryId)}/content/video`,
  );
  return res.data.data;
};

/**
 * GET /api/v1/roster/{entry_id}/threads/{type}/messages
 *
 * Cursor-paginated thread messages. Response envelope is
 * `{ data: { messages: [...], next_cursor } }` — extracting from
 * `res.data.data` (the inner wrapper) is what the legacy helper got
 * wrong; that bug silently returned empty threads for every consumer.
 */
const getRosterThread = async (
  entryId: string,
  type: ThreadType,
  params: { cursor?: string; limit?: number } = {},
): Promise<ThreadMessagesPage> => {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const url = `/roster/${encodeURIComponent(entryId)}/threads/${type}/messages${
    qs.toString() ? `?${qs.toString()}` : ''
  }`;
  const res = await apiClient.get<DataResponse<ThreadMessagesPage>>(url);
  return res.data.data;
};

/**
 * GET /api/v1/roster/{entry_id}/threads/{type}/anchors?version_id=...
 *
 * The FULL, unpaginated set of anchored messages for one script version —
 * unlike getRosterThread, which windows chat history and would silently
 * drop anchors older than its most recent page from the Script tab's
 * persistent highlights on any thread with more than a page of chat.
 */
const getAnchoredMessages = async (
  entryId: string,
  type: ThreadType,
  versionId: string,
): Promise<MessageView[]> => {
  const url = `/roster/${encodeURIComponent(entryId)}/threads/${type}/anchors?version_id=${encodeURIComponent(versionId)}`;
  const res = await apiClient.get<DataResponse<{ messages: MessageView[] }>>(url);
  return res.data.data.messages;
};

/**
 * POST /api/v1/roster/{entry_id}/threads/{type}/messages
 *
 * Appends a message to the row's thread. Returns the new MessageView
 * (BE persists it + assigns id + timestamps). Throws on 423 if the
 * thread is locked (caller catches + surfaces "thread closed" UI).
 */
const postRosterMessage = async (
  entryId: string,
  type: ThreadType,
  body: string,
  anchor?: MessageAnchor,
  replyTo?: string,
): Promise<MessageView> => {
  const res = await apiClient.post<DataResponse<MessageView>>(
    `/roster/${encodeURIComponent(entryId)}/threads/${type}/messages`,
    {
      body,
      ...(anchor ? { anchor } : {}),
      ...(replyTo ? { reply_to: replyTo } : {}),
    },
  );
  return res.data.data;
};

/**
 * PATCH /api/v1/roster/{entry_id}/threads/{type}/messages/{message_id}
 *
 * Edits a message the caller authored. Returns the updated MessageView
 * (edited=true). Throws on 403 (not author), 404 (gone), or 423 (locked).
 */
const editRosterMessage = async (
  entryId: string,
  type: ThreadType,
  messageId: string,
  body: string,
): Promise<MessageView> => {
  const res = await apiClient.patch<DataResponse<MessageView>>(
    `/roster/${encodeURIComponent(entryId)}/threads/${type}/messages/${encodeURIComponent(messageId)}`,
    { body },
  );
  return res.data.data;
};

/**
 * DELETE /api/v1/roster/{entry_id}/threads/{type}/messages/{message_id}
 *
 * Hard-deletes a message. Allowed for the author or a Discovr admin.
 * Returns 204. Throws on 403, 404, or 423 (locked).
 */
const deleteRosterMessage = async (
  entryId: string,
  type: ThreadType,
  messageId: string,
): Promise<void> => {
  await apiClient.delete(
    `/roster/${encodeURIComponent(entryId)}/threads/${type}/messages/${encodeURIComponent(messageId)}`,
  );
};

/**
 * POST /api/v1/roster/{entry_id}/threads/content/messages — note variant.
 * Same endpoint as postRosterMessage, with a media anchor and a client_id
 * so a retried send returns the original (200) instead of duplicating.
 */
const postRosterNote = async (entryId: string, req: PostNoteRequest): Promise<MessageView> => {
  const res = await apiClient.post<DataResponse<MessageView>>(
    `${contentThreadPath(entryId)}/messages`,
    req,
  );
  return res.data.data;
};

/**
 * POST /api/v1/roster/{entry_id}/threads/{type}/read
 *
 * Marks the thread as read for the calling user up to
 * `lastReadMessageId` (or the thread's latest message when omitted).
 * Returns 204; nothing to read.
 */
const markRosterThreadRead = async (
  entryId: string,
  type: ThreadType,
  lastReadMessageId?: string,
): Promise<void> => {
  await apiClient.post(
    `/roster/${encodeURIComponent(entryId)}/threads/${type}/read`,
    lastReadMessageId ? { last_read_message_id: lastReadMessageId } : {},
  );
};

/**
 * POST /api/v1/roster/{entry_id}/{phase}/seen
 *
 * Marks the entry's current script/content version as opened by the calling
 * user — clears their floating "New" badge for that version. Idempotent.
 */
const markPostSeen = async (entryId: string, phase: 'script' | 'content'): Promise<void> => {
  await apiClient.post(`/roster/${encodeURIComponent(entryId)}/${phase}/seen`, {});
};

/**
 * GET /api/v1/roster/{entry_id}/script/versions — the archive listing.
 * Newest first; first row is the live version.
 */
const getScriptVersions = (entryId: string): Promise<ScriptVersionListing> =>
  apiClient
    .get<DataResponse<ScriptVersionListing>>(
      `/roster/${encodeURIComponent(entryId)}/script/versions`,
    )
    .then((res) => res.data.data);

/**
 * GET /api/v1/roster/{entry_id}/script/versions/{version_id} — one stored
 * revision with its markdown body (read-only archive view).
 */
const getScriptVersion = (entryId: string, versionId: string): Promise<ScriptVersionDetail> =>
  apiClient
    .get<DataResponse<ScriptVersionDetail>>(
      `/roster/${encodeURIComponent(entryId)}/script/versions/${encodeURIComponent(versionId)}`,
    )
    .then((res) => res.data.data);

/**
 * GET /api/v1/campaigns/{id}/roster/live-content
 *
 * Returns the Live-content roster page (live_pending + live). Each
 * row carries the live_video sub-doc only when admin has pasted the
 * URL — that's the phase discriminator on the FE (nil → live_pending,
 * non-nil → live).
 */
const listLiveContent = (
  campaignId: string,
  params: UnifiedListParams = {},
): Promise<PagedResult<RosterEntryLive>> =>
  apiClient
    .get<PaginatedResponse<RosterEntryLive>>(
      `/campaigns/${encodeURIComponent(campaignId)}/roster/live-content?${unifiedRosterQs(params)}`,
    )
    .then((r) => parsePaged<RosterEntryLive>(r.data));

/**
 * GET /api/v1/campaigns/{id}/roster/rejected
 *
 * Returns the Rejected-tab roster page. Terminal phase — read-only on
 * the FE; deliverables are returned with is_selected flags preserved
 * so the dropdown component renders disabled with the pre-rejection
 * pick highlighted.
 */
const listRejected = (
  campaignId: string,
  params: UnifiedListParams = {},
): Promise<PagedResult<RosterEntryRejected>> =>
  apiClient
    .get<PaginatedResponse<RosterEntryRejected>>(
      `/campaigns/${encodeURIComponent(campaignId)}/roster/rejected?${unifiedRosterQs(params)}`,
    )
    .then((r) => parsePaged<RosterEntryRejected>(r.data));

// ─── Sentiment ──────────────────────────────────────────────────────────────

/**
 * GET /api/v1/roster/{entry_id}/sentiment
 *
 * Per-roster-entry daily sentiment series.
 */
const getRosterSentiment = (
  rosterEntryId: string,
  fromDate?: string,
  toDate?: string,
) =>
  getOrNullOn404<SentimentRosterDailyResponse>(
    `/roster/${encodeURIComponent(rosterEntryId)}/sentiment${dateRangeQs({ fromDate, toDate })}`,
  );

// ─── Creator analytics + brand-safety ───────────────────────────────────────

/**
 * GET /api/v1/roster/{entry_id}/analytics/metrics
 *
 * Per-roster-entry creator analytics envelope (status + opaque data
 * payload). BE returns `{ status, data }`. 404/error → null so the
 * row-hover analytics panel falls back to its "no data yet" view.
 */
const getCreatorAnalytics = async (
  entryId: string,
): Promise<{ status: string; data?: Record<string, unknown> } | null> => {
  try {
    const res = await apiClient.get<DataResponse<{
      status: string;
      data?: Record<string, unknown>;
    }>>(`/roster/${encodeURIComponent(entryId)}/analytics/metrics`);
    return res.data.data ?? null;
  } catch {
    return null;
  }
};

/**
 * GET /api/v1/roster/{entry_id}/analytics/safety
 *
 * Per-roster-entry brand-safety analysis envelope. Same async-task
 * shape BrandSafetyWorkspace consumes via aiAnalyzeChannel; this hook
 * is used by row-hover surfaces that need to read the cached result
 * without re-running the analysis.
 */
const getCreatorBrandSafety = async (
  entryId: string,
): Promise<{
  task_id?: string;
  status: string;
  result?: Record<string, unknown>;
} | null> => {
  try {
    const res = await apiClient.get<DataResponse<{
      task_id?: string;
      status: string;
      data?: Record<string, unknown>;
      result?: Record<string, unknown>;
    }>>(`/roster/${encodeURIComponent(entryId)}/analytics/safety`);
    const body = res.data.data;
    if (!body) return null;
    const rawStatus = String(body.status ?? '').toLowerCase();
    const normalisedStatus =
      rawStatus === 'complete' || rawStatus === 'done' ? 'SUCCESS' : body.status;
    return {
      task_id: body.task_id,
      status: normalisedStatus,
      result: body.data ?? body.result,
    };
  } catch {
    return null;
  }
};

// ─── Creator resolver (shareable-link relanding) ────────────────────────────

/**
 * The result of resolving a username to a tab/pill/page position within a
 * campaign roster. Returned by the by-username resolver endpoint.
 *
 * `creator` is the full Creators-tab row so the hook can hydrate the
 * query cache directly without a second fetch.
 */
export interface ResolveCreatorResult {
  creator: RosterEntryCreators;
  /** `outreach` + `removed`: an admin's outreach-only removal (2.8 Go Task 16). */
  tab: 'creators' | 'confirmed' | 'outreach';
  pill: 'pending' | 'approved' | 'rejected' | 'all' | 'removed';
  page: number;
}

/**
 * GET /api/v1/campaigns/{id}/roster/by-username/{username}?limit=30
 *
 * Resolves which tab, pill, and page a creator lands on inside a campaign.
 * Returns `ResolveCreatorResult` on success; propagates a 404 `ApiError`
 * when the creator isn't in the campaign (caller decides how to surface it).
 */
const resolveCreatorByUsername = async (
  campaignId: string,
  username: string,
  // MUST match the page size the destination tab will actually render with —
  // the BE derives `page = rank/limit + 1` from this arg, so a mismatch lands
  // the user on the wrong page (resolver says page 3 at limit 20, the tab loads
  // at limit 30, and page 3 now means rows 61-90 → empty view, dialog never
  // opens). Page size is user-selectable now (?size=), so callers pass the
  // ACTIVE size; this default only covers callers that have none, and tracks
  // DEFAULT_PAGE_SIZE.
  limit = 25,
): Promise<ResolveCreatorResult> => {
  const qs = new URLSearchParams({ limit: String(limit) });
  const res = await apiClient.get<{ data: ResolveCreatorResult }>(
    `/campaigns/${encodeURIComponent(campaignId)}/roster/by-username/${encodeURIComponent(username)}?${qs.toString()}`,
  );
  return res.data.data;
};

export function createCampaignDetailApi() {
  return {
    // Campaign-level reads
    listCampaigns,
    getCampaign,
    updateCampaignStatus,
    updateCampaignFetchStatus,
    updateCampaignPipeline,
    getCurrentEditRequest,
    listEditRequestHistory,
    addFieldComment,
    // Roster lists + KPIs
    listCreators,
    getCreatorsKPI,
    listFinalized,
    getFinalizedKPI,
    getRosterCounts,
    listScript,
    listContent,
    listLiveContent,
    listRejected,
    // Per-entry detail + thread
    getScriptDetail,
    getContentDetail,
    getContentVideoUrl,
    getRosterThread,
    getAnchoredMessages,
    postRosterMessage,
    editRosterMessage,
    deleteRosterMessage,
    postRosterNote,
    markRosterThreadRead,
    markPostSeen,
    getScriptVersions,
    getScriptVersion,
    // Per-entry analytics + sentiment + brand-safety
    getRosterSentiment,
    getCreatorAnalytics,
    getCreatorBrandSafety,
    // Shareable-link resolver
    resolveCreatorByUsername,
  };
}

export type CampaignDetailApi = ReturnType<typeof createCampaignDetailApi>;
